import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getGuardiansPlacedChartData, exportQualityData } from '../services/qualityService';
import api from '../api';
import QualityChart from '../components/QualityChart';
import styles from '../assets/qualityGuardiansPlaced.module.css';

// Format date from YYYY-MM-DD to mm/dd/yyyy
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString; // Return as-is if invalid
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Format date from mm/dd/yyyy to YYYY-MM-DD
const formatDateForInput = (dateString) => {
  if (!dateString || !dateString.trim()) return '';
  
  // If already in YYYY-MM-DD format, validate and return
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return dateString;
    }
  }
  
  // Try to parse mm/dd/yyyy format
  const parts = dateString.split('/');
  if (parts.length === 3) {
    const month = parts[0].trim().padStart(2, '0');
    const day = parts[1].trim().padStart(2, '0');
    const year = parts[2].trim();
    
    // Validate the date
    const date = new Date(`${year}-${month}-${day}`);
    if (!isNaN(date.getTime())) {
      // Format as YYYY-MM-DD
      return `${year}-${month}-${day}`;
    }
  }
  
  // Try to parse as Date object and format
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // Return empty string if invalid
  return '';
};

const QualityGuardiansPlaced = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState(null);
  const [chartType, setChartType] = useState('line');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    compareDateFrom: '',
    compareDateTo: '',
    granularity: 'day',
    compare: false
  });
  const [activeDatePicker, setActiveDatePicker] = useState(null); // 'dateFrom', 'dateTo', 'compareDateFrom', 'compareDateTo', or null
  const [datePickerMonth, setDatePickerMonth] = useState(new Date().getMonth() + 1);
  const [datePickerYear, setDatePickerYear] = useState(new Date().getFullYear());
  const datePicker1Ref = useRef(null);
  const datePicker2Ref = useRef(null);
  const datePicker3Ref = useRef(null);
  const datePicker4Ref = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Ensure dates are in YYYY-MM-DD format for API
      const dateFrom = formatDateForInput(filters.dateFrom);
      const dateTo = formatDateForInput(filters.dateTo);
      
      console.log('📊 [GUARDIANS PLACED] Loading chart data:', {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        formattedDateFrom: dateFrom,
        formattedDateTo: dateTo,
        granularity: filters.granularity
      });
      
      // Load chart data using Laravel API format
      if (dateFrom && dateTo) {
        const groupBy = filters.granularity === 'day' ? 1 : filters.granularity === 'week' ? 2 : 3;
        try {
          console.log('📊 [GUARDIANS PLACED] Calling API with:', { dateFrom, dateTo, groupBy });
          const chartResponse = await getGuardiansPlacedChartData(
            dateFrom,
            dateTo,
            groupBy
          );
          
          console.log('📊 [GUARDIANS PLACED] API Response:', chartResponse);
          
          if (chartResponse && chartResponse.keys && chartResponse.values) {
            // Format labels to match Laravel format: "Wednesday, 11 Nov"
            // Laravel uses moment.js format: 'dddd, D MMM' for days
            const formatLabel = (dateString, granularity) => {
              if (!dateString) return '';
              
              console.log('🔍 [FORMAT LABEL] Input:', dateString, 'Granularity:', granularity);
              
              // Parse the date string (could be YYYY-MM-DD, YYYY-MM, YEARWEEK, or ISO format)
              let date;
              
              // Check if it's already an ISO timestamp (e.g., "2020-01-14T19:00:00.000Z")
              if (typeof dateString === 'string' && dateString.includes('T')) {
                // Already ISO format, parse directly and extract just the date part
                date = new Date(dateString);
                // For days granularity, we want just the date without time
                if (granularity === 1) {
                  // Create a new date with just the date components to avoid timezone issues
                  date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                }
              } else if (granularity === 1) {
                // Days: YYYY-MM-DD format from DATE(date)
                date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
              } else if (granularity === 2) {
                // Weeks: YEARWEEK format (e.g., 202045 = year 2020, week 45)
                // YEARWEEK returns: year * 100 + week number
                const yearWeek = parseInt(dateString);
                if (isNaN(yearWeek)) {
                  // If not a number, try parsing as date
                  date = new Date(dateString);
                } else {
                  const year = Math.floor(yearWeek / 100);
                  const week = yearWeek % 100;
                  // Calculate first day of week (Monday for YEARWEEK mode 1)
                  const jan1 = new Date(year, 0, 1);
                  const daysOffset = (week - 1) * 7;
                  // Adjust to Monday of that week (YEARWEEK mode 1 starts on Monday)
                  const dayOfWeek = jan1.getDay();
                  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday = 1
                  date = new Date(year, 0, 1 + daysOffset + mondayOffset);
                }
              } else if (granularity === 3) {
                // Months: YYYY-MM format from DATE_FORMAT(date, '%Y-%m')
                if (dateString.includes('T')) {
                  date = new Date(dateString);
                } else {
                  date = new Date(dateString + '-01T00:00:00');
                }
              } else {
                date = new Date(dateString);
              }
              
              if (isNaN(date.getTime())) {
                console.warn('⚠️ [FORMAT LABEL] Invalid date:', dateString);
                return dateString; // Return as-is if invalid
              }
              
              // Format based on granularity (matching Laravel moment.js format)
              const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              
              if (granularity === 1) {
                // Days: "Wednesday, 11 Nov" (Laravel: 'dddd, D MMM')
                const dayName = days[date.getDay()];
                const day = date.getDate();
                const month = months[date.getMonth()];
                return `${dayName}, ${day} ${month}`;
              } else if (granularity === 2) {
                // Weeks: "11 Nov, 45th week of 2020" (Laravel: 'D MMM, Wo [week of] YYYY')
                // Calculate week number properly
                const year = date.getFullYear();
                const jan1 = new Date(year, 0, 1);
                const daysSinceJan1 = Math.floor((date - jan1) / (24 * 60 * 60 * 1000));
                const week = Math.ceil((daysSinceJan1 + jan1.getDay() + 1) / 7);
                const day = date.getDate();
                const month = months[date.getMonth()];
                return `${day} ${month}, ${week}${getOrdinalSuffix(week)} week of ${year}`;
              } else {
                // Months: "Nov 2020" (Laravel: 'MMM YYYY')
                const month = months[date.getMonth()];
                const year = date.getFullYear();
                return `${month} ${year}`;
              }
            };
            
            // Helper function for ordinal suffixes
            const getOrdinalSuffix = (num) => {
              const j = num % 10;
              const k = num % 100;
              if (j === 1 && k !== 11) return 'st';
              if (j === 2 && k !== 12) return 'nd';
              if (j === 3 && k !== 13) return 'rd';
              return 'th';
            };
            
            // Format all labels
            const formattedLabels = chartResponse.keys.map(key => {
              const formatted = formatLabel(key, groupBy);
              console.log('📅 [LABEL FORMAT] Original:', key, '→ Formatted:', formatted);
              return formatted;
            });
            
            console.log('📅 [LABEL FORMAT] All formatted labels:', formattedLabels);
            
            // Transform Laravel format to Chart.js format
            const datasets = [{
              label: 'Placed Guardians',
              data: chartResponse.values,
              borderColor: '#20c997',
              backgroundColor: 'rgba(32, 201, 151, 0.1)',
              fill: true,
              borderWidth: 2
            }];

            // Add comparison data if enabled
            if (filters.compare && filters.compareDateFrom && filters.compareDateTo) {
              try {
                const compareDateFrom = formatDateForInput(filters.compareDateFrom);
                const compareDateTo = formatDateForInput(filters.compareDateTo);
                const compareResponse = await getGuardiansPlacedChartData(
                  compareDateFrom,
                  compareDateTo,
                  groupBy
                );
                if (compareResponse && compareResponse.values) {
                  datasets.push({
                    label: 'Previous Period',
                    data: compareResponse.values,
                    borderColor: '#dc3545',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    fill: true,
                    borderWidth: 2
                  });
                }
              } catch (compareError) {
                console.error('Error loading comparison data:', compareError);
              }
            }

            const chartDataToSet = {
              labels: formattedLabels,
              datasets
            };
            
            console.log('📊 [CHART DATA] Setting chart data with labels:', chartDataToSet.labels);
            setChartData(chartDataToSet);
          } else {
            throw new Error('Invalid chart data format');
          }
        } catch (chartError) {
          console.error('❌ [GUARDIANS PLACED] Chart API error:', chartError);
          console.error('❌ [GUARDIANS PLACED] Error details:', {
            message: chartError.message,
            response: chartError.response?.data,
            status: chartError.response?.status
          });
          // Don't fallback to summary data - show empty chart instead
          setChartData(null);
          toast.error('Failed to load chart data. Please check your date range and try again.');
        }
      } else {
        // No dates provided - show empty chart
        console.log('⚠️ [GUARDIANS PLACED] No dates provided, showing empty chart');
        setChartData(null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error loading guardians placed data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Click outside handler for date pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePicker1Ref.current && !datePicker1Ref.current.contains(event.target) &&
        datePicker2Ref.current && !datePicker2Ref.current.contains(event.target) &&
        datePicker3Ref.current && !datePicker3Ref.current.contains(event.target) &&
        datePicker4Ref.current && !datePicker4Ref.current.contains(event.target)
      ) {
        setActiveDatePicker(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calendar generation and navigation functions
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const generateCalendarDays = (month, year) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const handlePrevMonth = () => {
    if (datePickerMonth === 1) {
      setDatePickerMonth(12);
      setDatePickerYear(datePickerYear - 1);
    } else {
      setDatePickerMonth(datePickerMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (datePickerMonth === 12) {
      setDatePickerMonth(1);
      setDatePickerYear(datePickerYear + 1);
    } else {
      setDatePickerMonth(datePickerMonth + 1);
    }
  };

  const handlePrevYear = () => {
    setDatePickerYear(datePickerYear - 1);
  };

  const handleNextYear = () => {
    setDatePickerYear(datePickerYear + 1);
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value);
    if (!isNaN(newYear)) {
      setDatePickerYear(newYear);
    }
  };

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value);
    if (!isNaN(newMonth) && newMonth >= 1 && newMonth <= 12) {
      setDatePickerMonth(newMonth);
    }
  };

  // Generate year options (current year ± 20 years)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 20; i <= currentYear + 20; i++) {
    years.push(i);
  }

  const handleDateSelect = (day, field) => {
    if (day === null) return;
    
    const month = String(datePickerMonth).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const year = datePickerYear;
    const dateValue = `${year}-${month}-${dayStr}`;
    
    handleDateChange(field, dateValue);
    setActiveDatePicker(null);
  };

  const handleToday = (field) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateValue = `${year}-${month}-${day}`;
    
    handleDateChange(field, dateValue);
    setActiveDatePicker(null);
  };

  const handleClear = (field) => {
    handleDateChange(field, '');
    setActiveDatePicker(null);
  };

  const openDatePicker = (field) => {
    // Close any other open picker
    setActiveDatePicker(field);
    
    // Set calendar to show the current date value if available
    const currentValue = filters[field];
    if (currentValue) {
      const date = new Date(currentValue);
      if (!isNaN(date.getTime())) {
        setDatePickerMonth(date.getMonth() + 1);
        setDatePickerYear(date.getFullYear());
      }
    }
  };

  const calendarDays = generateCalendarDays(datePickerMonth, datePickerYear);


  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field, value) => {
    // Value from date input is in YYYY-MM-DD format
    setFilters(prev => ({
      ...prev,
      [field]: value // Store in YYYY-MM-DD format for API
    }));
  };

  const handleExport = async (type) => {
    try {
      if (type === 'pdf') {
        // Use specific PDF endpoint for guardians placed (matching Laravel)
        const dateFrom = formatDateForInput(filters.dateFrom);
        const dateTo = formatDateForInput(filters.dateTo);
        const compareDateFrom = filters.compareDateFrom ? formatDateForInput(filters.compareDateFrom) : null;
        const compareDateTo = filters.compareDateTo ? formatDateForInput(filters.compareDateTo) : null;

        const response = await api.post('/quality/guardians/placed/pdf', {
          datetimepicker1: dateFrom,
          datetimepicker2: dateTo,
          datetimepicker3: compareDateFrom,
          datetimepicker4: compareDateTo,
          dateFrom,
          dateTo,
          compareDateFrom,
          compareDateTo,
          granularity: filters.granularity || 'day'
        }, {
          responseType: 'blob' // Important: tell axios to expect binary data
        });

        // Check if response is actually a PDF blob
        if (response.data instanceof Blob) {
          // Create download link from blob response
          const url = window.URL.createObjectURL(response.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = `guardians_placed_${new Date().toISOString().split('T')[0]}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          toast.success('PDF export generated successfully');
        } else {
          throw new Error('Invalid PDF response format');
        }
      } else {
        // CSV export using generic endpoint
        const response = await exportQualityData({
          type,
          page: 'guardians_placed',
          ...filters
        });

        if (response.success) {
          toast.success(`${type.toUpperCase()} export generated successfully`);
          
          // Create download link
          const blob = new Blob([response.data.content], { 
            type: 'text/csv' 
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = response.data.filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          toast.error(`Failed to export ${type.toUpperCase()}`);
        }
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Error exporting ${type.toUpperCase()}: ${error.message}`);
    }
  };

  // Function to get X-axis labels based on granularity
  const getXAxisLabels = () => {
    // Use actual chart data labels if available
    if (chartData && chartData.labels && Array.isArray(chartData.labels)) {
      return chartData.labels;
    }
    
    // Fallback to default labels based on granularity
    switch (filters.granularity) {
      case 'day':
        return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      case 'week':
        return ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'];
      case 'month':
        return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      default:
        return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    }
  };

  // Function to get X-axis label based on granularity
  const getXAxisLabel = () => {
    switch (filters.granularity) {
      case 'day':
        return 'Day';
      case 'week':
        return 'Week';
      case 'month':
        return 'Month';
      default:
        return 'Day';
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb - Match Laravel exactly */}
      <div className={styles.breadcrumb}>
        <span onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
        <span className={styles.separator}>/</span>
        <span className={styles.current}>Guardians Placed</span>
      </div>

      {/* Chart Controls - Match Laravel exactly */}
      <div className={styles.chartControls}>
        <div className={styles.dateInputs}>
          <div className={styles.dateInput} ref={datePicker1Ref}>
            <input
              type="text"
              placeholder="mm/dd/yyyy"
              value={formatDateForDisplay(filters.dateFrom)}
              onChange={(e) => {
                const formatted = formatDateForInput(e.target.value);
                handleFilterChange('dateFrom', formatted);
              }}
              className={styles.dateField}
              onClick={() => openDatePicker('dateFrom')}
              readOnly
            />
            <i 
              className={`${styles.calendarIcon} glyphicon glyphicon-calendar`}
              onClick={(e) => {
                e.stopPropagation();
                openDatePicker('dateFrom');
              }}
              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
            ></i>
            {activeDatePicker === 'dateFrom' && (
              <div className={styles.datePicker} style={{ zIndex: 1000 }}>
                <div className={styles.calendarHeader}>
                  <button 
                    type="button"
                    className={styles.calendarNavButton}
                    onClick={handlePrevMonth}
                    aria-label="Previous month"
                  >
                    ▲
                  </button>
                  <div className={styles.calendarMonthYear}>
                    <select
                      className={styles.calendarMonthSelect}
                      value={datePickerMonth}
                      onChange={handleMonthChange}
                    >
                      {monthNames.map((month, index) => (
                        <option key={index} value={index + 1}>{month}</option>
                      ))}
                    </select>
                    <select
                      className={styles.calendarYearSelect}
                      value={datePickerYear}
                      onChange={handleYearChange}
                    >
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <button 
                    type="button"
                    className={styles.calendarNavButton}
                    onClick={handleNextMonth}
                    aria-label="Next month"
                  >
                    ▼
                  </button>
                </div>
                <div className={styles.calendarDaysHeader}>
                  {dayNames.map(day => (
                    <div key={day} className={styles.calendarDayHeader}>{day}</div>
                  ))}
                </div>
                <div className={styles.calendarDaysGrid}>
                  {calendarDays.map((day, index) => (
                    <div
                      key={index}
                      className={`${styles.calendarDay} ${day === null ? styles.calendarDayEmpty : ''} ${
                        day && filters.dateFrom && 
                        new Date(filters.dateFrom).getDate() === day &&
                        new Date(filters.dateFrom).getMonth() + 1 === datePickerMonth &&
                        new Date(filters.dateFrom).getFullYear() === datePickerYear
                          ? styles.calendarDaySelected : ''
                      }`}
                      onClick={() => handleDateSelect(day, 'dateFrom')}
                    >
                      {day}
                    </div>
                  ))}
                </div>
                <div className={styles.calendarFooter}>
                  <button 
                    type="button"
                    className={styles.calendarFooterButton}
                    onClick={() => handleClear('dateFrom')}
                  >
                    Clear
                  </button>
                  <button 
                    type="button"
                    className={styles.calendarFooterButton}
                    onClick={() => handleToday('dateFrom')}
                  >
                    Today
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className={styles.dateInput} ref={datePicker2Ref}>
            <input
              type="text"
              placeholder="mm/dd/yyyy"
              value={formatDateForDisplay(filters.dateTo)}
              onChange={(e) => {
                const formatted = formatDateForInput(e.target.value);
                handleFilterChange('dateTo', formatted);
              }}
              className={styles.dateField}
              onClick={() => openDatePicker('dateTo')}
              readOnly
            />
            <i 
              className={`${styles.calendarIcon} glyphicon glyphicon-calendar`}
              onClick={(e) => {
                e.stopPropagation();
                openDatePicker('dateTo');
              }}
              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
            ></i>
            {activeDatePicker === 'dateTo' && (
              <div className={styles.datePicker} style={{ zIndex: 1000 }}>
                <div className={styles.calendarHeader}>
                  <button 
                    type="button"
                    className={styles.calendarNavButton}
                    onClick={handlePrevMonth}
                    aria-label="Previous month"
                  >
                    ▲
                  </button>
                  <div className={styles.calendarMonthYear}>
                    <select
                      className={styles.calendarMonthSelect}
                      value={datePickerMonth}
                      onChange={handleMonthChange}
                    >
                      {monthNames.map((month, index) => (
                        <option key={index} value={index + 1}>{month}</option>
                      ))}
                    </select>
                    <select
                      className={styles.calendarYearSelect}
                      value={datePickerYear}
                      onChange={handleYearChange}
                    >
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <button 
                    type="button"
                    className={styles.calendarNavButton}
                    onClick={handleNextMonth}
                    aria-label="Next month"
                  >
                    ▼
                  </button>
                </div>
                <div className={styles.calendarDaysHeader}>
                  {dayNames.map(day => (
                    <div key={day} className={styles.calendarDayHeader}>{day}</div>
                  ))}
                </div>
                <div className={styles.calendarDaysGrid}>
                  {calendarDays.map((day, index) => (
                    <div
                      key={index}
                      className={`${styles.calendarDay} ${day === null ? styles.calendarDayEmpty : ''} ${
                        day && filters.dateTo && 
                        new Date(filters.dateTo).getDate() === day &&
                        new Date(filters.dateTo).getMonth() + 1 === datePickerMonth &&
                        new Date(filters.dateTo).getFullYear() === datePickerYear
                          ? styles.calendarDaySelected : ''
                      }`}
                      onClick={() => handleDateSelect(day, 'dateTo')}
                    >
                      {day}
                    </div>
                  ))}
                </div>
                <div className={styles.calendarFooter}>
                  <button 
                    type="button"
                    className={styles.calendarFooterButton}
                    onClick={() => handleClear('dateTo')}
                  >
                    Clear
                  </button>
                  <button 
                    type="button"
                    className={styles.calendarFooterButton}
                    onClick={() => handleToday('dateTo')}
                  >
                    Today
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.granularityControls}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="granularity"
              value="day"
              checked={filters.granularity === 'day'}
              onChange={(e) => handleFilterChange('granularity', e.target.value)}
              className={styles.radioInput}
            />
            <span className={styles.radioText}>Day</span>
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="granularity"
              value="week"
              checked={filters.granularity === 'week'}
              onChange={(e) => handleFilterChange('granularity', e.target.value)}
              className={styles.radioInput}
            />
            <span className={styles.radioText}>Week</span>
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="granularity"
              value="month"
              checked={filters.granularity === 'month'}
              onChange={(e) => handleFilterChange('granularity', e.target.value)}
              className={styles.radioInput}
            />
            <span className={styles.radioText}>Month</span>
          </label>
        </div>

        <div className={styles.compareControl}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={filters.compare}
              onChange={(e) => handleFilterChange('compare', e.target.checked)}
              className={styles.checkboxInput}
            />
            <span className={styles.checkboxText}>Compare to another time</span>
          </label>
        </div>
      </div>

      {/* Compare Date Pickers Row - Match Laravel exactly */}
      {filters.compare && (
        <div className={styles.compareDateRow}>
          <div className={styles.dateInputs}>
            <div className={styles.dateInput} ref={datePicker3Ref}>
              <input
                type="text"
                placeholder="mm/dd/yyyy"
                value={formatDateForDisplay(filters.compareDateFrom)}
                onChange={(e) => {
                  const formatted = formatDateForInput(e.target.value);
                  handleFilterChange('compareDateFrom', formatted);
                }}
                className={styles.dateField}
                onClick={() => openDatePicker('compareDateFrom')}
                readOnly
              />
              <i 
                className={`${styles.calendarIcon} glyphicon glyphicon-calendar`}
                onClick={(e) => {
                  e.stopPropagation();
                  openDatePicker('compareDateFrom');
                }}
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
              ></i>
              {activeDatePicker === 'compareDateFrom' && (
                <div className={styles.datePicker} style={{ zIndex: 1000 }}>
                  <div className={styles.calendarHeader}>
                    <button 
                      type="button"
                      className={styles.calendarNavButton}
                      onClick={handlePrevMonth}
                      aria-label="Previous month"
                    >
                      ▲
                    </button>
                    <div className={styles.calendarMonthYear}>
                      <select
                        className={styles.calendarMonthSelect}
                        value={datePickerMonth}
                        onChange={handleMonthChange}
                      >
                        {monthNames.map((month, index) => (
                          <option key={index} value={index + 1}>{month}</option>
                        ))}
                      </select>
                      <select
                        className={styles.calendarYearSelect}
                        value={datePickerYear}
                        onChange={handleYearChange}
                      >
                        {years.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                    <button 
                      type="button"
                      className={styles.calendarNavButton}
                      onClick={handleNextMonth}
                      aria-label="Next month"
                    >
                      ▼
                    </button>
                  </div>
                  <div className={styles.calendarDaysHeader}>
                    {dayNames.map(day => (
                      <div key={day} className={styles.calendarDayHeader}>{day}</div>
                    ))}
                  </div>
                  <div className={styles.calendarDaysGrid}>
                    {calendarDays.map((day, index) => (
                      <div
                        key={index}
                        className={`${styles.calendarDay} ${day === null ? styles.calendarDayEmpty : ''} ${
                          day && filters.compareDateFrom && 
                          new Date(filters.compareDateFrom).getDate() === day &&
                          new Date(filters.compareDateFrom).getMonth() + 1 === datePickerMonth &&
                          new Date(filters.compareDateFrom).getFullYear() === datePickerYear
                            ? styles.calendarDaySelected : ''
                        }`}
                        onClick={() => handleDateSelect(day, 'compareDateFrom')}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className={styles.calendarFooter}>
                    <button 
                      type="button"
                      className={styles.calendarFooterButton}
                      onClick={() => handleClear('compareDateFrom')}
                    >
                      Clear
                    </button>
                    <button 
                      type="button"
                      className={styles.calendarFooterButton}
                      onClick={() => handleToday('compareDateFrom')}
                    >
                      Today
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.dateInput} ref={datePicker4Ref}>
              <input
                type="text"
                placeholder="mm/dd/yyyy"
                value={formatDateForDisplay(filters.compareDateTo)}
                onChange={(e) => {
                  const formatted = formatDateForInput(e.target.value);
                  handleFilterChange('compareDateTo', formatted);
                }}
                className={styles.dateField}
                onClick={() => openDatePicker('compareDateTo')}
                readOnly
              />
              <i 
                className={`${styles.calendarIcon} glyphicon glyphicon-calendar`}
                onClick={(e) => {
                  e.stopPropagation();
                  openDatePicker('compareDateTo');
                }}
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
              ></i>
              {activeDatePicker === 'compareDateTo' && (
                <div className={styles.datePicker} style={{ zIndex: 1000 }}>
                  <div className={styles.calendarHeader}>
                    <button 
                      type="button"
                      className={styles.calendarNavButton}
                      onClick={handlePrevMonth}
                      aria-label="Previous month"
                    >
                      ▲
                    </button>
                    <div className={styles.calendarMonthYear}>
                      <select
                        className={styles.calendarMonthSelect}
                        value={datePickerMonth}
                        onChange={handleMonthChange}
                      >
                        {monthNames.map((month, index) => (
                          <option key={index} value={index + 1}>{month}</option>
                        ))}
                      </select>
                      <select
                        className={styles.calendarYearSelect}
                        value={datePickerYear}
                        onChange={handleYearChange}
                      >
                        {years.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                    <button 
                      type="button"
                      className={styles.calendarNavButton}
                      onClick={handleNextMonth}
                      aria-label="Next month"
                    >
                      ▼
                    </button>
                  </div>
                  <div className={styles.calendarDaysHeader}>
                    {dayNames.map(day => (
                      <div key={day} className={styles.calendarDayHeader}>{day}</div>
                    ))}
                  </div>
                  <div className={styles.calendarDaysGrid}>
                    {calendarDays.map((day, index) => (
                      <div
                        key={index}
                        className={`${styles.calendarDay} ${day === null ? styles.calendarDayEmpty : ''} ${
                          day && filters.compareDateTo && 
                          new Date(filters.compareDateTo).getDate() === day &&
                          new Date(filters.compareDateTo).getMonth() + 1 === datePickerMonth &&
                          new Date(filters.compareDateTo).getFullYear() === datePickerYear
                            ? styles.calendarDaySelected : ''
                        }`}
                        onClick={() => handleDateSelect(day, 'compareDateTo')}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className={styles.calendarFooter}>
                    <button 
                      type="button"
                      className={styles.calendarFooterButton}
                      onClick={() => handleClear('compareDateTo')}
                    >
                      Clear
                    </button>
                    <button 
                      type="button"
                      className={styles.calendarFooterButton}
                      onClick={() => handleToday('compareDateTo')}
                    >
                      Today
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chart Area - Match Laravel exactly */}
      <div className={styles.chartArea}>
        <div className={styles.chartGrid}>
          {/* Y-axis labels */}
          <div className={styles.yAxis}>
            <div className={styles.yAxisLabel}>Placed Guardians</div>
            <div className={styles.yLabel}>10</div>
            <div className={styles.yLabel}>8</div>
            <div className={styles.yLabel}>6</div>
            <div className={styles.yLabel}>4</div>
            <div className={styles.yLabel}>2</div>
            <div className={styles.yLabel}>0</div>
          </div>

          {/* Chart content */}
          <div className={styles.chartContent}>
            <div className={styles.chartCanvas}>
              {chartData && chartData.labels && chartData.datasets ? (
                <div style={{ width: '100%', height: '400px', position: 'relative' }}>
                  <QualityChart 
                    data={chartData} 
                    chartType={chartType}
                    height={400}
                  />
                </div>
              ) : (
                <div className={styles.chartGridLines}>
                  {/* Grid lines will be rendered by CSS */}
                </div>
              )}
            </div>
          </div>

          {/* X-axis labels - Only show when chart is not rendered */}
          {(!chartData || !chartData.labels || !chartData.datasets) && (
            <div className={styles.xAxis}>
              {getXAxisLabels().map((label, index) => (
                <div key={index} className={styles.xLabel}>{label}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart Type Selection - Match Laravel exactly */}
      <div className={styles.chartTypeControls}>
        <button 
          className={`${styles.chartTypeButton} ${chartType === 'line' ? styles.active : ''}`}
          onClick={() => setChartType('line')}
        >
          Line
        </button>
        <button 
          className={`${styles.chartTypeButton} ${chartType === 'bar' ? styles.active : ''}`}
          onClick={() => setChartType('bar')}
        >
          Bar
        </button>
        <button 
          className={styles.chartTypeButton}
          onClick={() => handleExport('pdf')}
        >
          Pdf
        </button>
      </div>

      {/* Legend - Match Laravel exactly */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: '#20c997' }}></div>
          <span>Placed Guardians</span>
        </div>
        {filters.compare && (
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: '#dc3545' }}></div>
            <span>Previous Period</span>
          </div>
        )}
        <div className={styles.legendMonth}>{getXAxisLabel()}</div>
      </div>
    </div>
  );
};

export default QualityGuardiansPlaced; 